import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';

interface Channel {
  id: string;
  instance_id: string;
  channel_type: string;
  name: string;
  endpoint: string | null;
  config: Record<string, unknown> | null;
  status: string;
  api_key: string | null;
  rate_limit: number | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

const CHANNEL_TYPES = ['WEB', 'API', 'MCP', 'WEBSOCKET', 'GRPC'];
const STATUS_OPTIONS = ['active', 'inactive', 'error'];

export default function ChannelAdmin() {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [filterInstanceId, setFilterInstanceId] = useState('');

  const [createForm, setCreateForm] = useState({
    instance_id: '',
    channel_type: 'API',
    name: '',
    endpoint: '',
    rate_limit: '100',
    is_public: false,
  });

  const [editForm, setEditForm] = useState({
    name: '',
    endpoint: '',
    status: 'active',
    rate_limit: '100',
    is_public: false,
  });

  useEffect(() => {
    // 初始加载（无筛选条件）；内联 promise 链避免 effect 内同步 setState
    apiClient
      .get('/channels', { params: {} })
      .then((res) => setChannels(res.data))
      .catch((e) => console.error('Failed to load channels:', e))
      .finally(() => setLoading(false));
  }, []);

  const loadChannels = async () => {
    try {
      const params: { instance_id?: string } = {};
      if (filterInstanceId.trim()) {
        params.instance_id = filterInstanceId.trim();
      }
      const res = await apiClient.get('/channels', { params });
      setChannels(res.data);
    } catch (e) {
      console.error('Failed to load channels:', e);
    } finally {
      setLoading(false);
    }
  };

  const createChannel = async () => {
    if (!createForm.instance_id.trim() || !createForm.name.trim()) {
      toast.error('Instance ID 和名称为必填项');
      return;
    }
    try {
      const res = await apiClient.post('/channels', {
        instance_id: createForm.instance_id,
        channel_type: createForm.channel_type,
        name: createForm.name,
        endpoint: createForm.endpoint || undefined,
        rate_limit: parseInt(createForm.rate_limit) || undefined,
        is_public: createForm.is_public,
      });
      setChannels([res.data, ...channels]);
      setShowCreate(false);
      setCreateForm({
        instance_id: '',
        channel_type: 'API',
        name: '',
        endpoint: '',
        rate_limit: '100',
        is_public: false,
      });
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      toast.error('创建失败: ' + (typeof detail === 'string' ? detail : (e as Error).message));
    }
  };

  const startEdit = (channel: Channel) => {
    setEditId(channel.id);
    setEditForm({
      name: channel.name,
      endpoint: channel.endpoint || '',
      status: channel.status,
      rate_limit: String(channel.rate_limit || ''),
      is_public: channel.is_public,
    });
  };

  const saveEdit = async (id: string) => {
    try {
      const body: {
        name: string;
        endpoint?: string;
        status: string;
        is_public: boolean;
        rate_limit?: number;
      } = {
        name: editForm.name,
        endpoint: editForm.endpoint || undefined,
        status: editForm.status,
        is_public: editForm.is_public,
      };
      if (editForm.rate_limit) {
        body.rate_limit = parseInt(editForm.rate_limit);
      }
      const res = await apiClient.patch(`/channels/${id}`, body);
      setChannels(channels.map(c => (c.id === id ? res.data : c)));
      setEditId(null);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      toast.error('更新失败: ' + (typeof detail === 'string' ? detail : (e as Error).message));
    }
  };

  const deleteChannel = async (id: string) => {
    if (!(await confirm({ title: '确定要删除这个 Channel 吗？', message: '此操作不可恢复。', danger: true }))) return;
    try {
      await apiClient.delete(`/channels/${id}`);
      setChannels(channels.filter(c => c.id !== id));
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      toast.error('删除失败: ' + (typeof detail === 'string' ? detail : (e as Error).message));
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      active: 'success',
      inactive: 'error',
      error: 'error',
    };
    const labels: Record<string, string> = {
      active: '活跃',
      inactive: '停用',
      error: '错误',
    };
    return <span className={`badge ${badges[status] || 'info'}`}>{labels[status] || status}</span>;
  };

  const getTypeBadge = (type: string) => {
    return <span className="badge info">{type}</span>;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <h1>Channel Admin</h1>
        <button className="primary" onClick={() => setShowCreate(!showCreate)}>
          + 新建 Channel
        </button>
      </div>

      <div
        className="card mb-4"
        style={{
          borderColor: 'var(--color-warning)',
          background: 'rgba(251, 191, 36, 0.08)',
          color: 'var(--color-warning)',
          fontSize: 13,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>Channel 管理属于旧部署体系（依赖 Instance），新 AgentRuntime 体系下通常无需配置 Channel。除非需要维护历史实例，否则请使用「运行时大盘」和「Agent 部署管理」。</span>
      </div>

      <div className="card mb-4">
        <div className="flex items-center gap-3">
          <div style={{ flex: 1 }}>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>按 Instance ID 过滤</label>
            <input
              value={filterInstanceId}
              onChange={e => setFilterInstanceId(e.target.value)}
              placeholder="输入 Instance ID 过滤..."
              onKeyDown={e => e.key === 'Enter' && loadChannels()}
            />
          </div>
          <button className="primary" onClick={loadChannels} style={{ marginTop: 20 }}>
            过滤
          </button>
          {filterInstanceId && (
            <button className="secondary" onClick={() => { setFilterInstanceId(''); loadChannels(); }} style={{ marginTop: 20 }}>
              清除
            </button>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="card mb-4">
          <h3>创建新 Channel</h3>
          <div className="flex flex-col gap-3">
            <input
              value={createForm.instance_id}
              onChange={e => setCreateForm({ ...createForm, instance_id: e.target.value })}
              placeholder="Instance ID (UUID) *"
            />
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Channel 类型</label>
              <select
                value={createForm.channel_type}
                onChange={e => setCreateForm({ ...createForm, channel_type: e.target.value })}
                style={{ width: '100%' }}
              >
                {CHANNEL_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <input
              value={createForm.name}
              onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
              placeholder="Channel 名称 *"
            />
            <input
              value={createForm.endpoint}
              onChange={e => setCreateForm({ ...createForm, endpoint: e.target.value })}
              placeholder="Endpoint URL（可选）"
            />
            <div className="flex items-center gap-4">
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Rate Limit (次/分钟)</label>
                <input
                  type="number"
                  value={createForm.rate_limit}
                  onChange={e => setCreateForm({ ...createForm, rate_limit: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20 }}>
                <input
                  type="checkbox"
                  id="create-is-public"
                  checked={createForm.is_public}
                  onChange={e => setCreateForm({ ...createForm, is_public: e.target.checked })}
                />
                <label htmlFor="create-is-public">公开 Channel</label>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="primary" onClick={createChannel}>创建</button>
              <button className="secondary" onClick={() => setShowCreate(false)}>取消</button>
            </div>
          </div>
        </div>
      )}

      {channels.length === 0 ? (
        <div className="empty">
          <h3>暂无 Channel</h3>
          <p>点击"新建 Channel"开始创建</p>
        </div>
      ) : (
        <div className="list">
          {channels.map(channel => (
            <div key={channel.id} className="list-item">
              {editId === channel.id ? (
                <div className="list-item-content" style={{ width: '100%' }}>
                  <div className="flex flex-col gap-3">
                    <input
                      value={editForm.name}
                      onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                      placeholder="名称"
                    />
                    <input
                      value={editForm.endpoint}
                      onChange={e => setEditForm({ ...editForm, endpoint: e.target.value })}
                      placeholder="Endpoint URL"
                    />
                    <div className="flex gap-4">
                      <div style={{ flex: 1 }}>
                        <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>状态</label>
                        <select
                          value={editForm.status}
                          onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                          style={{ width: '100%' }}
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Rate Limit</label>
                        <input
                          type="number"
                          value={editForm.rate_limit}
                          onChange={e => setEditForm({ ...editForm, rate_limit: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="edit-is-public"
                        checked={editForm.is_public}
                        onChange={e => setEditForm({ ...editForm, is_public: e.target.checked })}
                      />
                      <label htmlFor="edit-is-public">公开 Channel</label>
                    </div>
                    <div className="flex gap-2">
                      <button className="primary" onClick={() => saveEdit(channel.id)}>保存</button>
                      <button className="secondary" onClick={() => setEditId(null)}>取消</button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="list-item-content">
                    <div className="flex items-center gap-2">
                      <h3 style={{ margin: 0 }}>{channel.name}</h3>
                      {getTypeBadge(channel.channel_type)}
                      {getStatusBadge(channel.status)}
                      {channel.is_public && <span className="badge success">公开</span>}
                    </div>
                    <p style={{ margin: '4px 0 0' }}>
                      <span className="text-muted">
                        Instance: {channel.instance_id.substring(0, 8)}...
                      </span>
                      {channel.endpoint && (
                        <span className="text-muted"> · Endpoint: {channel.endpoint}</span>
                      )}
                      {channel.rate_limit && (
                        <span className="text-muted"> · 限流: {channel.rate_limit}/min</span>
                      )}
                    </p>
                    <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                      创建于: {new Date(channel.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="list-item-actions" style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                    <button className="secondary" onClick={() => startEdit(channel)} style={{ padding: '6px 12px' }}>
                      编辑
                    </button>
                    <button className="secondary" onClick={() => deleteChannel(channel.id)} style={{ padding: '6px 12px' }}>
                      删除
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {dialog}
    </div>
  );
}
